import { useQuery } from '@tanstack/react-query';
import { formatDay, formatGestation, parseIsoDay } from '@wodi/domain';
import type { PatientBanner as BannerView, PayerKind, VISIT_TYPES } from '@wodi/shared';
import { Button, LoadingBlock, Notice, PatientBanner, type BannerAllergies } from '@wodi/ui';

import { ApiError } from '../api/client.ts';
import { patientBannerQuery } from '../api/patients.ts';
import { formatInstant } from './format.ts';

export type VisitType = (typeof VISIT_TYPES)[number];

export const VISIT_TYPE_LABELS: Record<VisitType, string> = {
  outpatient: 'Outpatient',
  walk_in: 'Walk-in',
  inpatient: 'Inpatient',
  emergency: 'Emergency',
  mortuary: 'Mortuary',
};

export const PAYER_LABELS: Record<PayerKind, string> = {
  cash: 'Cash',
  sha: 'SHA',
  insurer: 'Insurance',
  corporate: 'Corporate',
  other: 'Other payer',
};

/**
 * "Pregnant, EDD 6 Dec 2026, 24 weeks 3 days": dates and weeks only, never a category
 * (docs/plans/phase2-maternity-mch.md §0).
 */
export function pregnancyWords(pregnancy: NonNullable<BannerView['pregnancy']>): string {
  const parts = ['Pregnant'];
  if (pregnancy.edd !== null) {
    const day = parseIsoDay(pregnancy.edd);
    if (day.ok) parts.push(`EDD ${formatDay(day.value)}`);
  }
  if (pregnancy.gestation !== null) parts.push(formatGestation(pregnancy.gestation));
  // A passive cue only, never a category (TODO(clinical-verify) M23: the weeks are a setting).
  const cue = pregnancy.pastEddCue ? '. Past EDD, check pregnancy status' : '';
  return `${parts.join(', ')}${cue}`;
}

/** The API's allergy summary, in the banner's terms. Both have exactly three states. */
export function bannerAllergies(summary: BannerView['allergies']): BannerAllergies {
  if (summary.state === 'list') {
    const [first, ...rest] = summary.allergies.map((a) => ({
      substance: a.substance,
      ...(a.reaction === null ? {} : { reaction: a.reaction }),
    }));
    // The schema guarantees at least one; if not, say "not recorded", never blank.
    return first === undefined
      ? { state: 'not_recorded' }
      : { state: 'list', allergies: [first, ...rest] };
  }
  return { state: summary.state };
}

export function PatientBannerView({ banner, linked }: { banner: BannerView; linked: boolean }) {
  return (
    <PatientBanner
      name={banner.displayName}
      sex={banner.sex.abbreviation ?? banner.sex.display}
      age={banner.age}
      ageEstimated={banner.ageEstimated}
      patientNumber={banner.patientNumber}
      crId={banner.crId}
      registryStatus={banner.registrySync.message}
      {...(banner.openVisit === null
        ? {}
        : {
            payer:
              banner.openVisit.payerDetail === null
                ? PAYER_LABELS[banner.openVisit.payerKind]
                : `${PAYER_LABELS[banner.openVisit.payerKind]} · ${banner.openVisit.payerDetail}`,
            visit: {
              number: banner.openVisit.visitNumber,
              type: VISIT_TYPE_LABELS[banner.openVisit.type],
            },
          })}
      allergies={bannerAllergies(banner.allergies)}
      {...(banner.identity.state === 'unknown' && banner.identity.temporaryLabel !== null
        ? { unidentified: { temporaryLabel: banner.identity.temporaryLabel } }
        : {})}
      alsoRecordedAs={banner.mergedFrom.map((m) => m.temporaryLabel)}
      {...(banner.deceased === null
        ? {}
        : { deceased: { diedAt: formatInstant(banner.deceased.diedAt) } })}
      {...(banner.pregnancy === null ? {} : { pregnancy: pregnancyWords(banner.pregnancy) })}
      precautions={banner.precautions}
      {...(linked ? { href: `/patients/${banner.id}` } : {})}
    />
  );
}

/**
 * The patient banner for a patient-context screen, loaded from GET /patients/:id/banner.
 * Every patient screen puts this first (CLAUDE.md: the banner is on every patient
 * screen). While it loads or if it fails, the screen says so in its place.
 */
export function PatientBannerLoader({
  patientId,
  linked = true,
}: {
  patientId: string;
  /** Link the name to the patient page; off on the patient page itself. */
  linked?: boolean;
}) {
  const banner = useQuery(patientBannerQuery(patientId));
  // No margin of its own: the screen's gap spaces the banner from what follows, like cards.
  return (
    <div>
      {banner.isPending ? <LoadingBlock label="Loading the patient" rows={2} /> : null}
      {banner.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void banner.refetch()}>
              Try again
            </Button>
          }
        >
          {banner.error instanceof ApiError
            ? banner.error.message
            : "The patient's details couldn't be loaded. Try again."}
        </Notice>
      ) : null}
      {banner.isSuccess ? <PatientBannerView banner={banner.data} linked={linked} /> : null}
    </div>
  );
}
